import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { type CheckoutAttemptTrackingContext, type ReviewDialogParams, trackReviewConfirmed, trackReviewDialogClosed, trackReviewDialogOpened } from '../trackers/checkout-funnel';

/**
 * The checkout-funnel wiring every V2 review dialog repeats:
 *
 * - fires `review_dialog_opened` ONCE, deferred until `isReady` (so `txFlowType` /
 *   `totalSteps` reflect the real requirements rather than the build-in-progress state);
 * - `trackClose` mirrors `review_dialog_closed` — call it from the dialog's close handler;
 * - `confirmAttempt` stamps a fresh `attemptId`, fires `review_confirmed`, and returns the
 *   {@link CheckoutAttemptTrackingContext} to thread into `submitTransactions`, which keeps
 *   step-resolution, cancellation, and conversion tracking alive through `ConfirmDialogV2`.
 *
 * The ref keeps the impression to one fire per mount, including dev StrictMode's double-invoke.
 */
export const useReviewFunnelTracking = (params, t0) => {
  const $ = _c(11);
  const isReady = t0 === undefined ? true : t0;
  const hasTrackedOpen = useRef(false);
  let t1;
  let t2;
  if ($[0] !== isReady || $[1] !== params) {
    t1 = () => {
      if (!isReady || hasTrackedOpen.current) {
        return;
      }
      hasTrackedOpen.current = true;
      trackReviewDialogOpened(params);
    };
    t2 = [isReady, params];
    $[0] = isReady;
    $[1] = params;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== params) {
    t3 = () => trackReviewDialogClosed(params);
    $[4] = params;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const trackClose = t3;
  let t4;
  if ($[6] !== params) {
    t4 = () => {
      const tracking = {
        ...params,
        attemptId: crypto.randomUUID(),
        totalSteps: params.totalSteps ?? 1
      };
      trackReviewConfirmed(tracking);
      return tracking;
    };
    $[6] = params;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const confirmAttempt = t4;
  let t5;
  if ($[8] !== confirmAttempt || $[9] !== trackClose) {
    t5 = {
      trackClose,
      confirmAttempt
    };
    $[8] = confirmAttempt;
    $[9] = trackClose;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};